import { useState } from 'react';
import { Plus, X, Check, Edit2, Trash2 } from 'lucide-react';
import { Input, Button } from '@/components/ui';
import { useSettingsStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import type { Category } from '@perfin/shared';

interface SubCategoryListProps {
  category: Category;
}

export function SubCategoryList({ category }: SubCategoryListProps) {
  const { addSubCat, editSubCat, removeSubCat } = useSettingsStore();
  const toast = useToast();
  const { confirmDelete } = useConfirm();

  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!newName.trim()) return;

    setSaving(true);
    try {
      await addSubCat(category.id, newName.trim());
      setNewName('');
      setAdding(false);
      toast.success('Sub-category added');
    } catch {
      toast.error('Failed to add sub-category');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (subCategoryId: string) => {
    if (!editingName.trim()) return;

    setSaving(true);
    try {
      await editSubCat(category.id, subCategoryId, editingName.trim());
      setEditingId(null);
      setEditingName('');
      toast.success('Sub-category updated');
    } catch {
      toast.error('Failed to update sub-category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (subCategoryId: string, subCategoryName: string) => {
    confirmDelete({
      title: 'Delete Sub-category',
      message: `Are you sure you want to delete "${subCategoryName}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await removeSubCat(category.id, subCategoryId);
          toast.success('Sub-category removed');
        } catch {
          toast.error('Failed to remove sub-category');
        }
      },
    });
  };

  const startEditing = (subCategoryId: string, currentName: string) => {
    setEditingId(subCategoryId);
    setEditingName(currentName);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName('');
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-700">Sub-categories</h4>
        {!adding && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setAdding(true)}
            leftIcon={<Plus className="h-3 w-3" />}
          >
            Add
          </Button>
        )}
      </div>

      {/* Sub-category list */}
      <div className="space-y-1">
        {category.subCategories.map((sub) => (
          <div
            key={sub.id}
            className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-gray-100"
          >
            {editingId === sub.id ? (
              <>
                <Input
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  className="flex-1 py-1 text-sm"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleEdit(sub.id);
                    if (e.key === 'Escape') cancelEditing();
                  }}
                />
                <button
                  onClick={() => handleEdit(sub.id)}
                  disabled={saving}
                  className="p-1 text-green-600 hover:bg-green-50 rounded"
                >
                  <Check className="h-4 w-4" />
                </button>
                <button
                  onClick={cancelEditing}
                  className="p-1 text-gray-400 hover:bg-gray-200 rounded"
                >
                  <X className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm text-gray-700">{sub.name}</span>
                <button
                  onClick={() => startEditing(sub.id, sub.name)}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
                >
                  <Edit2 className="h-3 w-3" />
                </button>
                <button
                  onClick={() => handleDelete(sub.id, sub.name)}
                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Add new sub-category */}
      {adding && (
        <div className="flex items-center gap-2 mt-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Sub-category name"
            className="flex-1 py-1 text-sm"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAdd();
              if (e.key === 'Escape') {
                setAdding(false);
                setNewName('');
              }
            }}
          />
          <button
            onClick={handleAdd}
            disabled={saving || !newName.trim()}
            className="p-1 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              setAdding(false);
              setNewName('');
            }}
            className="p-1 text-gray-400 hover:bg-gray-200 rounded"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {category.subCategories.length === 0 && !adding && (
        <p className="text-sm text-gray-400 italic">No sub-categories</p>
      )}
    </div>
  );
}
